/**
 * Image Loading Utilities
 * 
 * Centralized helpers for loading images for the spin-wheel library.
 * Used by both Portal preview and Experience App to ensure consistent behavior.
 * 
 * Based on spin-wheel library recommendations:
 * https://github.com/CrazyTim/spin-wheel/blob/release/images/examples/themes/js/index.js
 */

/**
 * Load an image from a URL and return an HTMLImageElement.
 * The spin-wheel library requires actual Image objects, not URLs.
 * 
 * @param url - Image URL (can be Firebase Storage URL or any valid image URL)
 * @returns Promise that resolves with the loaded HTMLImageElement
 * @throws Error if image fails to load
 * 
 * @example
 * ```typescript
 * const bgImage = await loadWheelImage('https://storage.googleapis.com/.../background.png');
 * const wheelProps = {
 *   items: [...],
 *   image: bgImage,  // HTMLImageElement, not string!
 * };
 * ```
 */
export function loadWheelImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    // Set up success handler
    img.onload = () => {
      console.log('✅ Wheel image loaded:', url);
      resolve(img);
    };
    
    // Set up error handler
    img.onerror = (error) => {
      console.error('❌ Failed to load wheel image:', url, error);
      reject(new Error(`Failed to load image from: ${url}`));
    };
    
    // Enable CORS if needed (Firebase Storage supports CORS)
    img.crossOrigin = 'anonymous';
    
    // Start loading the image
    img.src = url;
  });
}

/**
 * Load multiple images concurrently.
 * Useful when loading both background and overlay images.
 * 
 * @param urls - Array of image URLs to load
 * @returns Promise that resolves with array of loaded HTMLImageElements
 * @throws Error if any image fails to load
 * 
 * @example
 * ```typescript
 * const [bgImage, overlayImage] = await loadWheelImages([
 *   'https://storage.googleapis.com/.../background.png',
 *   'https://storage.googleapis.com/.../overlay.png'
 * ]);
 * ```
 */
export async function loadWheelImages(urls: string[]): Promise<HTMLImageElement[]> {
  console.log(`🖼️ Loading ${urls.length} wheel images...`);
  const images = await Promise.all(urls.map(url => loadWheelImage(url)));
  console.log(`✅ All ${urls.length} wheel images loaded`);
  return images;
}

/**
 * Safely load an optional image (returns undefined if URL is null/empty).
 * Useful for optional wheel images like background or overlay.
 * 
 * @param url - Image URL or null/undefined
 * @returns Promise that resolves with HTMLImageElement or undefined
 * 
 * @example
 * ```typescript
 * const bgImage = await loadOptionalWheelImage(settings.image);  // undefined if no URL
 * const wheelProps = {
 *   items: [...],
 *   image: bgImage,  // Can be undefined
 * };
 * ```
 */
export async function loadOptionalWheelImage(url: string | null | undefined): Promise<HTMLImageElement | undefined> {
  if (!url || url.trim() === '') {
    return undefined;
  }
  
  try {
    return await loadWheelImage(url);
  } catch (error) {
    console.warn('⚠️ Optional wheel image failed to load, continuing without it:', url);
    return undefined;
  }
}

/**
 * Preload fonts required by the wheel.
 * The spin-wheel library may need fonts to be loaded before rendering.
 * 
 * @param fontFamilies - Array of font family names to preload
 * @returns Promise that resolves when fonts are loaded
 * 
 * @example
 * ```typescript
 * await preloadWheelFonts(['Codec Pro', 'Arial']);
 * ```
 */
export async function preloadWheelFonts(fontFamilies: string[]): Promise<void> {
  if (!document.fonts) {
    console.warn('⚠️ Font Loading API not supported in this browser');
    return;
  }

  console.log(`🔤 Preloading fonts:`, fontFamilies);
  
  try {
    await Promise.all(
      fontFamilies.map(family => document.fonts.load(`16px "${family}"`))
    );
    console.log('✅ Fonts loaded successfully');
  } catch (error) {
    console.warn('⚠️ Some fonts failed to load, continuing anyway:', error);
  }
}
